import type { Scenario } from './types'

// Fictional illustrative case. Authored content — not derived from any real user.
// HLA-DQ2.5 + IL-6 + TNF-alpha + CTLA4 — four specialists, no diagnosis, one coherent picture.
// The aha: the diagnostic system is organized by organ. Chioma's disease is organized by inflammation.
// The false-negative tTG-IgA (reduced gluten before the test) is the pivot the whole case turns on.

export const autoimmuneChioma: Scenario = {
  slug: 'autoimmune-chioma',
  series: 'Autoimmune & Inflammation',
  title: "Every specialist saw a piece. No one saw the pattern.",
  socialHook:
    "Chioma, 41, spent four years being told what she did not have. Her joints, gut, brain fog, mouth ulcers, and low nutrients were treated as separate problems. Together, they pointed in one direction.",

  meta: {
    description:
      "A fictional case study: Chioma had four years of joint pain, fatigue, brain fog, mouth ulcers, gut symptoms, and no clear diagnosis. Her HLA-DQ2.5 result, reduced gluten before testing, low ferritin, low vitamin D, low B12, and inflammatory variants made the missed pattern visible.",
  },

  persona: {
    name: 'Chioma',
    age: 41,
    sex: 'Female',
    ancestry: 'Nigerian-British',
    occupation: 'Secondary school teacher',
    chiefConcern:
      "For four years, Chioma has had joint pain in her knees and fingers, fatigue that can stop her from finishing the school day, brain fog, mouth ulcers, bloating, loose stool, and cramping. Rheumatology said it was not lupus or RA. Gastroenterology said it was not Crohn's or UC and called it IBS by exclusion. Neurology said it was not MS. She takes no medication and has been told stress may be part of it.",
    familyHistory:
      "Maternal aunt: celiac disease. Mother: rheumatoid arthritis.",
  },

  clinicalPicture: {
    symptoms: [
      'Joint pain — knees and fingers, worse in the morning',
      'Fatigue severe enough to interrupt the school day',
      'Brain fog — difficulty word-finding and concentrating mid-afternoon',
      'Recurring mouth ulcers — several episodes per year, each lasting a week or two',
    ],
    labs: [
      {
        name: 'CRP (C-reactive protein)',
        value: '6.8 mg/L',
        refRange: '<1.0 mg/L',
        flag: 'high',
      },
      {
        name: 'tTG-IgA (anti-tissue transglutaminase)',
        value: '4.2 U/mL',
        refRange: '<7 U/mL',
        flag: 'normal',
      },
      {
        name: 'Ferritin',
        value: '11 µg/L',
        refRange: '20–200 µg/L',
        flag: 'low',
      },
      {
        name: 'Vitamin D (25-OH)',
        value: '9 ng/mL',
        refRange: '30–80 ng/mL',
        flag: 'low',
      },
      {
        name: 'Vitamin B12',
        value: '188 pg/mL',
        refRange: '200–900 pg/mL',
        flag: 'low',
      },
    ],
    medications: ['No current medications'],
    supplements: ['None'],
    lifestyle: [
      'Diet: varied, had informally reduced bread and pasta over the past 12 months after noticing gut symptoms improved',
      'Exercise: light walking, reduced due to fatigue and joint pain',
      'Alcohol: occasional — 1–2 drinks per week',
      'Sleep: disrupted — wakes with joint stiffness most mornings',
      'Non-smoker',
      'Occupation: secondary school teacher, on her feet most of the day',
    ],
  },

  genetics: [
    {
      gene: 'HLA-DQ2.5',
      variant: 'DQA1*05:01 / DQB1*02:01',
      rsid: undefined,
      genotype: 'HLA-DQ2.5 positive',
      plainMeaning:
        "Chioma carries the main genetic risk marker seen in most people with celiac disease. Her negative antibody test is less reassuring because she had already been cutting gluten for a year.",
      technicalDetail:
        "HLA-DQ2.5 is the strongest genetic risk factor for celiac disease — present in approximately 95% of confirmed cases versus around 30% of the general population. Not carrying it makes celiac very unlikely; carrying it does not confirm disease, but combined with a modified diet it makes a false-negative antibody result the expected finding. The tTG-IgA antibody is produced in response to gluten exposure — reduce gluten and antibody production falls, producing a borderline or negative result in someone with active disease. HLA-DQ2.5 positive + borderline antibody after dietary modification + three nutritional deficiencies consistent with small bowel malabsorption + family history of celiac constitutes a strong clinical case for a formal gluten challenge and repeat testing.",
    },
    {
      gene: 'IL-6',
      variant: 'rs1800795 (G>C, -174 promoter, high-producer variant)',
      rsid: 'rs1800795',
      genotype: 'High IL-6 producer',
      plainMeaning:
        "Chioma's body makes stronger inflammatory signals than average, so a gut trigger can show up as joint pain, fatigue, and brain fog.",
      technicalDetail:
        "IL-6 is the primary pro-inflammatory cytokine that translates local gut injury into systemic inflammation. The high-producer variant at rs1800795 means her IL-6 response is constitutively elevated — her body generates stronger and more sustained inflammatory signals from the same trigger. This directly explains the persistently elevated CRP and ESR that don't fit any single-organ diagnosis: the inflammation is not organ-specific because her inflammatory machinery runs hotter than average, and gluten acting as a continuous gut trigger is hitting an IL-6 system primed to amplify it.",
    },
    {
      gene: 'TNF-alpha',
      variant: 'rs1800629 (G>A, -308, high-producer variant)',
      rsid: 'rs1800629',
      genotype: 'High TNF-alpha producer',
      plainMeaning:
        "A second inflammatory amplifier helps explain why her joints, mouth, gut, and energy crash together instead of acting like separate problems.",
      technicalDetail:
        "TNF-alpha amplifies the IL-6 inflammatory cascade and drives tissue inflammation directly. Elevated TNF-alpha is specifically associated with joint pain, fatigue, and mucosal inflammation including mouth ulcers. Combined with her IL-6 high-producer variant, Chioma's cytokine profile converts any significant immune trigger into multi-system symptoms — which is why her joint pain, gut symptoms, fatigue, and oral ulcers all flare together rather than appearing as isolated problems.",
    },
    {
      gene: 'CTLA4',
      variant: 'rs3087243 (G allele, reduced T-cell downregulation)',
      rsid: 'rs3087243',
      genotype: 'Reduced CTLA4 brake function',
      plainMeaning:
        "Her immune system has a weaker-than-average brake pedal — so once inflammation starts, it takes longer to wind down, which explains why her symptoms linger long after an obvious flare.",
      technicalDetail:
        "CTLA4 acts as a brake on T-cell activation — it normally dampens immune responses after they have been triggered. A reduced-function variant at rs3087243 raises baseline T-cell activation and slows downregulation. This is associated with susceptibility to multiple autoimmune conditions and explains why her ANA is borderline positive and her immune system mounts responses readily. She does not meet the threshold for any single autoimmune diagnosis, but her T-cell regulation is constitutionally looser than average — making her more susceptible to immune activation from gut-derived triggers like gluten.",
    },
  ],

  beats: [
    {
      type: 'narrative',
      heading: "She planned her life around symptoms no one could name",
      body: "Chioma is a secondary school teacher. She plans lessons around pain, fatigue, and brain fog: which classes she can teach standing, which afternoons need lower mental load, which days might collapse. Over four years, each specialist looked at one part of the story. Rheumatology ruled out lupus and RA. Gastroenterology ruled out Crohn's and UC. Neurology ruled out MS. Her family doctor raised stress. She reduced stress. Nothing changed. The problem was not that no one looked carefully. It was that no one looked at everything together.",
    },
    {
      type: 'narrative',
      heading: "The clues sat in different folders",
      body: "Specialists are organized by body system: joints, gut, nerves. That works well for many diagnoses. Chioma's pattern did not stay in one system. Joint pain, gut symptoms, fatigue, brain fog, and mouth ulcers moved together. Her blood inflammation stayed high. Her celiac antibody was technically normal but close to the line. Her ferritin, vitamin D, and B12 were all low in a way that can fit poor absorption in the small bowel. Each clue looked incomplete alone. Together, they formed a pattern.",
    },
    {
      type: 'reveal',
      heading: "The normal celiac test had a catch",
      body: "Chioma's celiac antibody test looked normal. But for a year before the test, she had been eating less bread and pasta because her gut felt better without them. That detail changes the meaning of the result. The antibody test depends on gluten exposure. Eat less gluten, and the antibody can fall, even in someone who needs a proper celiac workup. Her HLA-DQ2.5 result does not diagnose celiac disease, but it keeps it firmly on the table. So do the low ferritin, low vitamin D, low B12, and family history. The missing step is a proper gluten challenge followed by repeat testing.",
    },
    {
      type: 'reveal',
      heading: "One trigger could explain many rooms of the house",
      body: "The gut clue still leaves a question: why joints, mouth ulcers, fatigue, and brain fog? Chioma's inflammatory genetics help connect those dots. Her IL-6 and TNF-alpha variants can turn a local trigger into a stronger whole-body signal. Her CTLA4 result suggests the immune brake may be slower to settle things down. That does not diagnose an autoimmune disease by itself. It explains why one ongoing trigger could feel like several separate illnesses at once.",
    },
    {
      type: 'action',
      heading: "Five clinical next steps",
      items: [
        "Ask her family doctor about a formal gluten challenge: normal gluten intake, such as at least 2 slices of bread or equivalent per day for 6 weeks, followed by repeat celiac antibodies and possible referral for small bowel biopsy.",
        "Treat the nutrient deficiencies while the investigation continues. Low ferritin, vitamin D, and B12 are clinically meaningful and should be rechecked after replacement, usually in 8–12 weeks.",
        "Bring the full pattern to her family doctor in writing: elevated inflammation, borderline celiac antibody after reduced gluten, HLA-DQ2.5, low ferritin, low vitamin D, low B12, and family history of celiac disease and rheumatoid arthritis.",
        "Use an anti-inflammatory eating pattern while the workup proceeds. A Mediterranean-style diet with olive oil, fish or algae omega-3s, legumes, vegetables, and minimal ultra-processed food fits her IL-6 and TNF-alpha profile.",
        "Track symptoms during the gluten challenge. Daily notes on gluten intake, gut symptoms, joint pain, fatigue, and brain fog can make the pattern easier for gastroenterology to evaluate.",
      ],
    },
  ],

  extraBeats: [
    {
      type: 'streak' as const,
      heading: "Eight weeks of tracking — through the gluten challenge and out the other side",
      caption:
        "Chioma tracked gluten challenge days, supplements, and symptoms for 8 weeks. The first three weeks were hardest because she was deliberately eating gluten again. After testing, she switched to strict gluten-free and started B12, iron, and vitamin D. By week 5, the improvement was visible.",
      goalLabel: "Gluten challenge tracking · B12 · ferritin · vitamin D daily",
      weeks: [
        {
          weekLabel: "Week 1",
          days: [true, true, false, true, true, false, true],
          note: "Starting the gluten challenge. Symptoms worse already — this is hard.",
        },
        {
          weekLabel: "Week 2",
          days: [true, true, true, true, false, true, true],
          note: "Worst joint pain in months. But I am documenting it all.",
        },
        {
          weekLabel: "Week 3",
          days: [true, false, true, true, true, true, false],
          note: "Gut symptoms bad. One missed day when I was too exhausted.",
        },
        {
          weekLabel: "Week 4",
          days: [true, true, true, true, true, false, true],
          note: "Challenge done. Switching to strict gluten-free today. Starting B12 and iron.",
        },
        {
          weekLabel: "Week 5",
          days: [true, true, true, false, true, true, true],
          note: "First week feeling slightly better. Brain fog lifting a little.",
        },
        {
          weekLabel: "Week 6",
          days: [true, true, true, true, true, true, false],
          note: "Energy improving. Joint pain noticeably better than last month.",
        },
        {
          weekLabel: "Week 7",
          days: [true, true, true, true, true, false, true],
          note: "Referral letter sent. Feeling hopeful for the first time in four years.",
        },
        {
          weekLabel: "Week 8",
          days: [true, true, true, true, true, true, true],
          note: "Perfect week. Gastro appointment confirmed. I finally have a direction.",
        },
      ],
    },
  ],

  cta: {
    namedPrompt: 'autoimmune-chioma',
    label: 'Explore your autoimmune genetics in Coach',
  },
}
